import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { IMG } from "@/lib/site";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const inputCls =
  "h-13 rounded-none border-white/15 bg-black/40 px-4 py-4 text-sm text-white placeholder:text-zinc-600 focus-visible:ring-trx-blue";

export default function Service() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", vehicle: "", service: "", preferred_date: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = "Schedule Service | TRX Auto Sales & Service Inc — Auto Repair Charlotte & Statesville NC";
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/appointments", form);
      setDone(true);
      toast.success("Appointment request received.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Service & Repair"
        lines={["Schedule", "Service."]}
        sub="Dealer-level diagnostics, maintenance, and repair — booked in under a minute. We'll confirm your appointment by phone or email."
        image={IMG.repairBay}
      />

      <section className="py-16 md:py-24" data-testid="service-form-section">
        <div className="mx-auto max-w-[900px] px-6 md:px-10">
          {done ? (
            <Reveal>
              <div className="border border-trx-blue/40 bg-card p-10 text-center md:p-16" data-testid="service-confirmation">
                <CheckCircle2 className="mx-auto h-12 w-12 text-trx-blue" />
                <h2 className="mt-6 font-display text-3xl md:text-4xl font-black tracking-tight text-white">
                  Request Received.
                </h2>
                <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-zinc-400">
                  Thanks, {form.name.split(" ")[0]}. Our team will reach out shortly to confirm your appointment.
                  Need us sooner? Call (704) 380-4555.
                </p>
              </div>
            </Reveal>
          ) : (
            <Reveal>
              <form onSubmit={submit} className="border border-white/10 bg-card p-8 md:p-12" data-testid="service-form">
                <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-white">Book Your Appointment</h2>
                <p className="mt-2 text-sm text-zinc-500">Mon–Fri 9–6 · Sat 9–2</p>

                <div className="mt-8 grid gap-5 md:grid-cols-2">
                  <Input required placeholder="Full name *" value={form.name} onChange={set("name")} className={inputCls} data-testid="service-name" />
                  <Input required placeholder="Phone *" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="service-phone" />
                  <Input required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} data-testid="service-email" />
                  <Input placeholder="Vehicle (year / make / model)" value={form.vehicle} onChange={set("vehicle")} className={inputCls} data-testid="service-vehicle" />
                  <select required value={form.service} onChange={set("service")} data-testid="service-type" className="h-13 border border-white/15 bg-black/40 px-4 text-sm text-zinc-300 outline-none focus:border-trx-blue">
                    <option value="" disabled>Service needed *</option>
                    <option>Diagnostics</option>
                    <option>Maintenance</option>
                    <option>Repair</option>
                    <option>Performance Upgrades</option>
                    <option>Pre-Purchase Inspection</option>
                    <option>Other</option>
                  </select>
                  <Input type="date" value={form.preferred_date} onChange={set("preferred_date")} className={`${inputCls} text-zinc-300`} data-testid="service-date" />
                </div>
                <Textarea
                  placeholder="Tell us what's going on with your vehicle…"
                  value={form.message}
                  onChange={set("message")}
                  className="mt-5 min-h-[130px] rounded-none border-white/15 bg-black/40 px-4 py-4 text-sm text-white placeholder:text-zinc-600 focus-visible:ring-trx-blue"
                  data-testid="service-message"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="service-submit"
                  className="mt-8 w-full bg-trx-blue px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition-all duration-300 hover:bg-white hover:text-black disabled:opacity-50 md:w-auto"
                >
                  {submitting ? "Submitting…" : "Request Appointment"}
                </button>
              </form>
            </Reveal>
          )}
        </div>
      </section>
    </>
  );
}
